/**
 * PALISADE CUSTOM — "Scroll to section" button action.
 *
 * Any element carrying `data-palisade-scroll-to="<css selector>"` scrolls the
 * page to that selector on click instead of navigating — and, which is the whole
 * point of the feature, WITHOUT appending a `#fragment` to the address bar.
 *
 * Delegated from the document rather than bound per element, so it covers
 * buttons rendered by any section or block, including ones the theme editor
 * injects after load.
 *
 * The trigger keeps its real `href` wherever there is one. If the selector is
 * not present on the current page, this handler stays out of the way and lets
 * the browser navigate normally — so a cross-page CTA (a header "Shop now"
 * pointing at /#buy-box, clicked from a blog article) still works, and the href
 * remains a genuine no-JS fallback.
 */

const TRIGGER_SELECTOR = '[data-palisade-scroll-to]';

/**
 * base.css sets `html { scroll-behavior: smooth }` unconditionally at :32 and
 * then only re-declares it behind `prefers-reduced-motion: no-preference` at
 * :2147 — a later non-matching rule does not undo an earlier matching one, so
 * the CSS never actually opts reduced-motion users out. `.page-wrapper` is
 * smooth unconditionally too. Deciding here keeps this feature correct
 * regardless of that.
 */
function scrollBehavior() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

/**
 * The value is merchant-entered, so an invalid selector must not throw on every
 * click for the rest of the session.
 */
function resolveTarget(rawSelector) {
  const selector = (rawSelector || '').trim();
  if (!selector) return null;

  try {
    return document.querySelector(selector);
  } catch {
    console.warn(`[palisade-scroll-to] Ignoring invalid CSS selector: ${selector}`);
    return null;
  }
}

/**
 * Land the section below the sticky header. `--header-height` is maintained live
 * on <body> by palisade-header.js. Skipped when the target already declares its
 * own scroll-margin-top, so the 90px on snippets/palisade-frame.liquid anchors
 * keeps winning.
 */
function applyHeaderOffset(target) {
  if (parseFloat(window.getComputedStyle(target).scrollMarginTop)) return;
  target.style.scrollMarginTop = 'calc(var(--header-height, 80px) + 1rem)';
}

/**
 * Send focus where sighted users were just sent. Without this the focus ring
 * stays on the CTA and the next Tab resumes from the header rather than the
 * section that was scrolled to (WCAG 2.4.3 Focus Order).
 *
 * Skipped for aria-hidden targets — palisade-frame.liquid anchors are empty
 * aria-hidden spans, and focusing one would drop focus onto a node that is
 * hidden from assistive tech.
 */
function moveFocus(target) {
  if (target.getAttribute('aria-hidden') === 'true') return;

  if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea')) {
    target.setAttribute('tabindex', '-1');
  }

  target.focus({ preventScroll: true });
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.(TRIGGER_SELECTOR);
  if (!trigger) return;

  // Leave modified clicks (open in new tab/window, middle click) to the browser.
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const target = resolveTarget(trigger.dataset.palisadeScrollTo);

  // Not on this page — fall through to the href so navigation still happens.
  if (!target) return;

  event.preventDefault();

  /**
   * Close an enclosing open <dialog> first. A trigger can live inside a drawer —
   * the cart drawer's empty-state "Continue shopping" scrolls to the buy box —
   * and scrolling the page behind a modal that is still covering it looks like
   * nothing happened. Only the ancestor dialog is touched, so a trigger out in
   * the page is unaffected.
   *
   * `close()` on the element rather than a synthetic Escape: Escape is
   * cancellable and a drawer may legitimately trap it, whereas close() also
   * fires the `close` event those components listen to for their own cleanup.
   */
  const dialog = trigger.closest('dialog[open]');
  if (dialog) dialog.close();

  applyHeaderOffset(target);

  /**
   * scrollIntoView, not window.scrollTo: above 990px base.css gives
   * `.page-wrapper` `height: 100dvh; overflow-y: auto` and locks html/body to
   * `overflow: hidden`, so the window is not the scrolling element on desktop
   * and window.scrollTo would silently do nothing. scrollIntoView walks up to
   * whichever ancestor actually scrolls.
   */
  target.scrollIntoView({
    behavior: scrollBehavior(),
    block: 'start',
    inline: 'nearest',
  });

  moveFocus(target);
});
